import { useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { groupUnitsForPicker, lookupUnit } from '../units'
import { IngredientPicker } from './IngredientPicker'
import { IngredientCreateModal } from './IngredientCreateModal'
import { IngredientEditModal } from './IngredientEditModal'
import { ModalShell } from './ModalShell'
import { ConfirmDialog } from './Dialog'
import type { IngredientRow, PantryItem } from '../types'

type Props = {
  open: boolean
  // When editing an existing item, the ingredient is fixed by item.ingredient_id.
  item: PantryItem | null
  // When creating, optionally seed the ingredient.
  seedIngredient?: IngredientRow | null
  seedQuantity?: number
  seedUnit?: string
  // When creating with no seeded ingredient, pre-fill the picker's search box.
  seedQuery?: string
  ingredients: IngredientRow[]
  onClose: () => void
  onSaved?: (row: PantryItem) => void
  onDeleted?: (id: number) => void
}

export function PantryItemEditModal({
  open, item, seedIngredient, seedQuantity, seedUnit, seedQuery,
  ingredients, onClose, onSaved, onDeleted,
}: Props) {
  // A filter-seeded query that exactly (case-insensitively) names an existing
  // ingredient should auto-select it — the user shouldn't have to re-pick, and
  // the canonical casing wins. Owned rows come first, so they're preferred.
  const seedMatch: IngredientRow | null =
    !item && !seedIngredient && seedQuery
      ? (ingredients.find(i => i.name.trim().toLowerCase() === seedQuery.trim().toLowerCase()) ?? null)
      : null

  const initialIng: IngredientRow | null =
    item ? (ingredients.find(i => i.id === item.ingredient_id) ?? null)
         : (seedIngredient ?? seedMatch)

  const today = new Date().toISOString().slice(0, 10)
  const expiresFromShelfLife = (purchased: string, shelfLife?: number) => {
    if (!purchased || !shelfLife) return ''
    const d = new Date(purchased + 'T00:00:00')
    d.setDate(d.getDate() + shelfLife)
    return d.toISOString().slice(0, 10)
  }

  const [ing, setIng] = useState<IngredientRow | null>(initialIng)
  // True when the picker holds text that doesn't resolve to a chosen ingredient.
  const [ingUnmatched, setIngUnmatched] = useState(false)
  const [quantity, setQuantity] = useState<string>(
    item ? String(item.quantity) :
    seedQuantity != null ? String(seedQuantity) : ''
  )
  const [unit, setUnit] = useState<string>(
    item?.unit || seedUnit || initialIng?.default_unit || 'ea'
  )
  const [purchasedAt, setPurchasedAt] = useState(
    item ? (item.purchased_at || '') : today
  )
  const [expiresAt, setExpiresAt] = useState(
    item ? (item.expires_at || '')
         : expiresFromShelfLife(today, initialIng?.shelf_life)
  )
  const [notes, setNotes] = useState(item?.notes || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState(false)
  const [viewingIng, setViewingIng] = useState<IngredientRow | null>(null)
  const [viewKey, setViewKey] = useState(0)

  // Open the Edit ingredient modal for the currently selected ingredient.
  // System rows are forked first so the modal opens in editable mode, matching
  // the openEdit behavior in PantryTab.
  const openIngredientView = async () => {
    if (!ing) return
    setError(null)
    try {
      let row = ing
      if (!ing.owned) {
        row = await api.adoptIngredient(ing.id)
        setIng(row)
      }
      setViewingIng(row)
      setViewKey(k => k + 1)
    } catch (e) {
      setError(String(e))
    }
  }

  const save = async () => {
    setError(null)
    const qty = Number(quantity)
    if (!item && !ing) {
      setError(ingUnmatched
        ? 'Please select an existing ingredient or add a new one'
        : 'Pick an ingredient.')
      return
    }
    if (!qty || qty <= 0) { setError('Quantity must be > 0.'); return }
    setSaving(true)
    try {
      let row: PantryItem
      if (item) {
        row = await api.updatePantryItem(item.id, {
          quantity: qty, unit,
          purchased_at: purchasedAt || undefined,
          expires_at: expiresAt || undefined,
          notes: notes || undefined,
        })
      } else if (ing!.component_recipe_id) {
        // Recipe lot: a batch of a recipe on hand, measured in the recipe's yield
        // unit (the backend stores it in that unit regardless of what we send).
        row = await api.createPantryItem({
          recipe_id: ing!.component_recipe_id, quantity: qty, unit: ing!.default_unit || 'serving',
          purchased_at: purchasedAt || undefined,
          expires_at: expiresAt || undefined,
          notes: notes || undefined,
        })
      } else {
        // Backend requires a user-owned ingredient so it can backfill defaults.
        // Adopt system rows here for the picker-driven "Add lot" path.
        let ingId = ing!.id
        if (!ing!.owned) {
          const adopted = await api.adoptIngredient(ing!.id)
          ingId = adopted.id
        }
        row = await api.createPantryItem({
          ingredient_id: ingId, quantity: qty, unit,
          purchased_at: purchasedAt || undefined,
          expires_at: expiresAt || undefined,
          notes: notes || undefined,
        })
      }
      onSaved?.(row)
      onClose()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  const del = async () => {
    if (!item) return
    setConfirmDel(false)
    setSaving(true)
    try {
      await api.deletePantryItem(item.id)
      onDeleted?.(item.id)
      onClose()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  const grouped = groupUnitsForPicker()

  return (
    <>
    <ModalShell
      open={open}
      onClose={onClose}
      zIndex={60}
      autoHeight
      panelStyle={{ width: 'min(440px, 100%)', padding: '18px 20px', gap: 12 }}
    >
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>{item ? 'Edit pantry lot' : 'New pantry lot'}</div>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 2,
          }}>
            <div style={{
              fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
              color: theme.ink, flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{ing?.name ?? item?.ingredient_name ?? 'Pick an ingredient'}</div>
            {ing && !ing.component_recipe_id && (
              <button onClick={openIngredientView} style={btnLink}>View</button>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        {!item && (
          <Field label="Ingredient">
            <IngredientPicker
              value={ing ? { ingredient_id: ing.id, name: ing.name, default_unit: ing.default_unit } : null}
              initialQuery={initialIng ? undefined : seedQuery}
              includeRecipes
              onChange={(row: IngredientRow) => {
                setIng(row)
                if (row.component_recipe_id) {
                  // Recipe lot — measured in the recipe's yield unit; no
                  // shelf-life-derived expiry.
                  setUnit(row.default_unit || 'serving')
                } else {
                  if (row.default_unit) setUnit(row.default_unit)
                  setExpiresAt(expiresFromShelfLife(purchasedAt || today, row.shelf_life))
                }
              }}
              onClear={() => setIng(null)}
              onUnmatchedChange={setIngUnmatched}
              onCreateRequest={(seed) => setCreating(seed)}
            />
          </Field>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Quantity">
            <input type="number" inputMode="decimal" min="0" step="any"
              value={quantity} onChange={e => setQuantity(e.target.value)}
              style={inputStyle}/>
          </Field>
          <Field label="Unit">
            {/* A recipe lot is measured in its recipe's yield unit, fixed here
                (not editable — it follows the recipe). */}
            {(ing?.component_recipe_id || item?.recipe_id) ? (
              <select value={unit || 'serving'} disabled style={{ ...inputStyle, appearance: 'auto' }}>
                <option value={unit || 'serving'}>{lookupUnit(unit)?.label ?? (unit || 'serving')}</option>
              </select>
            ) : (
              <select value={unit} onChange={e => setUnit(e.target.value)}
                style={{ ...inputStyle, appearance: 'auto' }}>
                <optgroup label="Mass">{grouped.mass.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
                <optgroup label="Volume">{grouped.volume.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
                <optgroup label="Count">{grouped.count.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}</optgroup>
              </select>
            )}
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Purchased">
            <input type="date" value={purchasedAt}
              onChange={e => setPurchasedAt(e.target.value)} style={inputStyle}/>
          </Field>
          <Field label="Expires">
            <input type="date" value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)} style={inputStyle}/>
          </Field>
        </div>

        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} style={inputStyle}/>
        </Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4 }}>
          <div>
            {item && (
              <button onClick={() => setConfirmDel(true)} disabled={saving} style={btnDanger}>Delete</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnSecondary}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
    </ModalShell>

    <ConfirmDialog
      open={confirmDel}
      message="Delete this pantry lot?"
      confirmLabel="Delete"
      danger
      onConfirm={del}
      onCancel={() => setConfirmDel(false)}
    />

    <IngredientEditModal
      key={viewKey}
      open={viewingIng !== null}
      ingredient={viewingIng}
      onCancel={() => setViewingIng(null)}
      onSaved={(row: IngredientRow) => {
        setIng(row)
        setViewingIng(null)
      }}
    />

    <IngredientCreateModal
      open={creating !== null} seedName={creating ?? ''}
      onCancel={() => setCreating(null)}
      onCreated={(row: IngredientRow) => {
        setIng(row)
        if (row.default_unit) setUnit(row.default_unit)
        setExpiresAt(expiresFromShelfLife(purchasedAt || today, row.shelf_life))
        setCreating(null)
      }}
    />
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  all: 'unset', boxSizing: 'border-box', width: '100%',
  padding: '7px 10px', borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`, background: theme.bg,
  fontFamily: theme.sans, fontSize: 13, color: theme.ink,
}
const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}
const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface, background: theme.ink,
}
const btnLink: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
  textTransform: 'uppercase', color: theme.accentInk,
  padding: '2px 6px', borderRadius: theme.radius,
}
const btnDanger: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'oklch(0.42 0.16 28)',
  border: `1px solid oklch(0.42 0.16 28 / 0.45)`,
}
